import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { Plus, Play, Check, X, RefreshCw, Factory, AlertTriangle, Clock, ClipboardList, Boxes, QrCode, ShieldCheck, FileWarning, Layers, Droplet, Hammer, Unlock, MapPin } from 'lucide-react';
import { Topbar } from '@/components/layout/Topbar';
import { Dialog } from '@/components/ui/Dialog';
import {
  useOrdres, useCreateOrdre, useStartOrdre, useCompleteOrdre, useCancelOrdre,
  useBetonProduits,
  useEstimation,
  useLotsByOrdre, useCreateLot, useUpdateLot, useDeleteLot, useVerifierNF,
  useCreateDNC,
  useMarkOrdreTask,
} from '@/hooks/useBetonCatalog';
import { useMoules, useEquipesProduction, useCommandes } from '@/hooks/useBetonCommandes';
import { usePlacerLotParc, useInventaireParc } from '@/hooks/useBetonSprint2';
import { STATUT_OF_LABELS, STATUT_LOT_LABELS, type StatutOF, type StatutLot, type OrdreFabrication, type LotProduction } from '@/types';
import { formatNumber } from '@/utils/currency';
import { extractError } from '@/api/client';
import { useAuthStore } from '@/stores/authStore';
import type { OrdreTaskName } from '@/api/operations';
import { demander } from '@/components/ui/Fenetres';

/**
 * Production béton — Sprint LB-B (MVP).
 * Liste des ordres de fabrication + création + actions start/complete/cancel.
 * Le complete déduit auto le stock MP via le BOM du produit.
 */

const fmtDate = (iso: string) => new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });

const STATUT_BADGE: Record<StatutOF, string> = {
  planifie: 'ds-bd ds-bd-n',
  en_cours: 'ds-bd ds-bd-i',
  termine:  'ds-bd ds-bd-s',
  annule:   'ds-bd ds-bd-d',
};

const TASK_LABELS: Record<OrdreTaskName, string> = {
  ferraillage: 'Ferraillage',
  coffrage:    'Coffrage',
  coulage:     'Coulage',
  decoffrage:  'Décoffrage',
};

const TASK_ORDER: OrdreTaskName[] = ['ferraillage', 'coffrage', 'coulage', 'decoffrage'];

const fmtShortDT = (iso: string) =>
  new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });

// ── Composant : workflow tâches préfa (4 chips cliquables) ──────────
const TaskProgress = ({
  of,
  onMark,
  disabled,
}: {
  of: OrdreFabrication;
  onMark: (task: OrdreTaskName) => void;
  disabled: boolean;
}) => {
  const ts: Record<OrdreTaskName, string | null> = {
    ferraillage: of.ferraillageAt,
    coffrage:    of.coffrageAt,
    coulage:     of.dateCoulage,
    decoffrage:  of.dateDecoffrageReelle,
  };
  const icons: Record<OrdreTaskName, ReactNode> = {
    ferraillage: <Layers   size={11} />,
    coffrage:    <Hammer   size={11} />,
    coulage:     <Droplet  size={11} />,
    decoffrage:  <Unlock   size={11} />,
  };
  return (
    <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
      {TASK_ORDER.map((t) => {
        const done = !!ts[t];
        const title = done ? `${TASK_LABELS[t]} le ${fmtShortDT(ts[t]!)}` : `Marquer ${TASK_LABELS[t]}`;
        return (
          <button
            key={t}
            type="button"
            className="ds-btn ds-btn-sm"
            onClick={() => !done && !disabled && onMark(t)}
            disabled={done || disabled}
            title={title}
            style={{
              padding: '2px 6px',
              fontSize: 10,
              gap: 3,
              borderColor: done ? 'var(--success)' : 'var(--bdr)',
              background:  done ? 'rgba(16,185,129,0.10)' : 'transparent',
              color:       done ? 'var(--success)' : 'var(--tx-3)',
              cursor:      done || disabled ? 'default' : 'pointer',
              opacity:     done || disabled ? (done ? 1 : 0.55) : 1,
            }}
          >
            {icons[t]}
            {TASK_LABELS[t].slice(0, 4)}
          </button>
        );
      })}
    </div>
  );
};

// ── Composant : countdown date décoffrage prévue ────────────────────
const CureCountdown = ({ of }: { of: OrdreFabrication }) => {
  if (!of.dateDecoffragePrevue) {
    return <span style={{ color: 'var(--tx-3)' }}>—</span>;
  }
  const prevue = new Date(of.dateDecoffragePrevue);
  const now    = new Date();
  const diffMs = prevue.getTime() - now.getTime();
  const diffDays = Math.ceil(diffMs / 86_400_000);
  const isReelle = !!of.dateDecoffrageReelle;
  const label = prevue.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' });

  if (isReelle) {
    return (
      <div>
        <div style={{ color: 'var(--success)', fontWeight: 600 }}>✓ {fmtShortDT(of.dateDecoffrageReelle!)}</div>
        <div style={{ fontSize: 10, color: 'var(--tx-3)' }}>prévu {label}</div>
      </div>
    );
  }
  const color = diffDays < 0 ? 'var(--danger)' : diffDays <= 1 ? 'var(--warning)' : 'var(--tx-2)';
  const suffix = diffDays < 0
    ? `(en retard de ${-diffDays}j)`
    : diffDays === 0
      ? '(aujourd\'hui)'
      : `(dans ${diffDays}j)`;
  return (
    <div style={{ color }}>
      <div style={{ fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: 10 }}>{suffix}</div>
    </div>
  );
};

export const ProductionPage = () => {
  const filiale = useAuthStore((s) => s.filiale);
  const [statut, setStatut]         = useState<StatutOF | ''>('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [lotsForOrdre, setLotsForOrdre] = useState<OrdreFabrication | null>(null);

  const { data, isLoading, refetch } = useOrdres({ statut: statut || undefined, limit: 100 });
  const start    = useStartOrdre();
  const complete = useCompleteOrdre();
  const cancel   = useCancelOrdre();
  const markTask = useMarkOrdreTask();

  const ordres = data?.data ?? [];

  const kpis = useMemo(() => ({
    planifie: ordres.filter((o) => o.statut === 'planifie').length,
    en_cours: ordres.filter((o) => o.statut === 'en_cours').length,
    termine:  ordres.filter((o) => o.statut === 'termine').length,
    annule:   ordres.filter((o) => o.statut === 'annule').length,
  }), [ordres]);

  const handleAction = async (fn: () => Promise<any>, confirmMsg?: string) => {
    if (confirmMsg && !(await demander.confirmer({ titre: confirmMsg, danger: true }))) return;
    setActionError(null);
    try { await fn(); }
    catch (err) { setActionError(extractError(err).message); }
  };

  return (
    <>
      <Topbar
        title="Production béton"
        subtitle={filiale ? `Ordres de fabrication · ${filiale.fullName}` : 'Production'}
        actions={
          <>
            <button className="ds-btn ds-btn-g ds-btn-sm" onClick={() => void refetch()} aria-label="Rafraîchir">
              <RefreshCw size={14} className={isLoading ? 'animate-spin' : ''} />
            </button>
            <button className="ds-btn ds-btn-p" onClick={() => setDialogOpen(true)}>
              <Plus size={14} /> Nouvel OF
            </button>
          </>
        }
      />

      <div className="ds-content">
        {actionError && (
          <div className="ds-alert-strip ds-alert-d" style={{ marginBottom: 16 }}>
            <span className="ds-alert-ico"><AlertTriangle size={18} /></span>
            <div className="ds-alert-txt">{actionError}</div>
          </div>
        )}

        {/* KPI strip 4 cards */}
        <div className="ds-kpi-strip">
          <div className="ds-ks ks-orange">
            <div className="ds-ks-label">Planifiés</div>
            <div className="ds-ks-value">{kpis.planifie}</div>
            <div className="ds-ks-sub">En attente démarrage</div>
          </div>
          <div className="ds-ks ks-blue">
            <div className="ds-ks-label">En cours</div>
            <div className="ds-ks-value">{kpis.en_cours}</div>
            <div className="ds-ks-sub">Production active</div>
          </div>
          <div className="ds-ks ks-green">
            <div className="ds-ks-label">Terminés</div>
            <div className="ds-ks-value">{kpis.termine}</div>
            <div className="ds-ks-sub">Lots produits</div>
          </div>
          <div className="ds-ks ks-red">
            <div className="ds-ks-label">Annulés</div>
            <div className="ds-ks-value">{kpis.annule}</div>
            <div className="ds-ks-sub">Sur période</div>
          </div>
        </div>

        {/* Filtres */}
        <div className="ds-filters">
          <select
            className="ds-fi"
            value={statut}
            onChange={(e) => setStatut(e.target.value as StatutOF | '')}
            style={{ width: 'auto' }}
          >
            <option value="">Tous statuts</option>
            {(Object.keys(STATUT_OF_LABELS) as StatutOF[]).map((s) => (
              <option key={s} value={s}>{STATUT_OF_LABELS[s]}</option>
            ))}
          </select>
        </div>

        {/* Table OF */}
        <div className="ds-table-panel">
          <div className="ds-table-header">
            <div>
              <div className="ds-table-title">Ordres de fabrication</div>
              <div className="ds-table-sub">{ordres.length} OF · Sprint LB-B</div>
            </div>
          </div>
          <table className="ds-tbl">
            <thead>
              <tr>
                <th style={{ width: 110 }}>Référence</th>
                <th>Produit · Moule · Équipe</th>
                <th className="ds-am" style={{ width: 90 }}>Quantité</th>
                <th style={{ width: 100 }}>Date prévue</th>
                <th style={{ width: 200 }}>Workflow préfa</th>
                <th style={{ width: 110 }}>Décoffrage prévu</th>
                <th style={{ width: 90 }}>Statut</th>
                <th style={{ width: 80 }}>Lots</th>
                <th style={{ width: 170 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr><td colSpan={9} style={{ textAlign: 'center', padding: 32, color: 'var(--tx-3)' }}>Chargement…</td></tr>
              )}
              {!isLoading && ordres.length === 0 && (
                <tr>
                  <td colSpan={9} style={{ textAlign: 'center', padding: 48 }}>
                    <Factory size={28} style={{ margin: '0 auto 8px', color: 'var(--tx-3)' }} />
                    <p style={{ fontSize: 13, color: 'var(--tx-3)' }}>Aucun ordre de fabrication</p>
                    <button className="ds-btn ds-btn-p ds-btn-sm" style={{ marginTop: 12 }} onClick={() => setDialogOpen(true)}>
                      <Plus size={12} /> Créer le premier OF
                    </button>
                  </td>
                </tr>
              )}
              {!isLoading && ordres.map((of) => (
                <tr key={of.id}>
                  <td>
                    <span className="ds-ref-code">{of.reference}</span>
                    {of.commande && <div style={{ fontSize: 10, color: 'var(--tx-3)', marginTop: 2 }}>↳ {of.commande.reference}</div>}
                  </td>
                  <td>
                    <div className="ds-entry-label">{of.produit?.designation ?? '—'}</div>
                    <div className="ds-entry-sub">
                      {of.produit?.code}
                      {of.moule && <span style={{ marginLeft: 6 }}>· 🔲 {of.moule.code}</span>}
                      {of.equipeProduction && <span style={{ marginLeft: 6 }}>· 👷 {of.equipeProduction.code}</span>}
                    </div>
                  </td>
                  <td className="ds-am">{formatNumber(parseFloat(of.quantite))} {of.produit?.uniteVente ?? ''}</td>
                  <td style={{ fontSize: 12, color: 'var(--tx-2)' }}>{fmtDate(of.datePlanifiee)}</td>
                  <td>
                    <TaskProgress
                      of={of}
                      onMark={(task) => handleAction(
                        () => markTask.mutateAsync({ id: of.id, task }),
                        `Marquer "${TASK_LABELS[task]}" pour ${of.reference} ?`,
                      )}
                      disabled={markTask.isPending || of.statut === 'annule' || of.statut === 'termine'}
                    />
                  </td>
                  <td style={{ fontSize: 11 }}>
                    <CureCountdown of={of} />
                  </td>
                  <td><span className={STATUT_BADGE[of.statut]}>{STATUT_OF_LABELS[of.statut]}</span></td>
                  <td>
                    <button
                      className="ds-btn ds-btn-g ds-btn-sm"
                      onClick={() => setLotsForOrdre(of)}
                      title="Voir / créer les lots"
                      disabled={of.statut === 'annule'}
                    >
                      <Boxes size={12} /> Lots
                    </button>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      {of.statut === 'planifie' && (
                        <>
                          <button
                            className="ds-btn ds-btn-g ds-btn-sm"
                            onClick={() => handleAction(() => start.mutateAsync(of.id), `Démarrer ${of.reference} ?`)}
                            disabled={start.isPending}
                            style={{ color: 'var(--info)' }}
                          >
                            <Play size={12} /> Démarrer
                          </button>
                          <button
                            className="ds-btn ds-btn-g ds-btn-sm"
                            onClick={() => handleAction(() => cancel.mutateAsync(of.id), `Annuler ${of.reference} ?`)}
                            disabled={cancel.isPending}
                            style={{ color: 'var(--danger)' }}
                          >
                            <X size={12} />
                          </button>
                        </>
                      )}
                      {of.statut === 'en_cours' && (
                        <>
                          <button
                            className="ds-btn ds-btn-p ds-btn-sm"
                            onClick={() => handleAction(
                              () => complete.mutateAsync({ id: of.id }),
                              `Terminer ${of.reference} ? Le stock MP sera déduit auto via le BOM produit.`,
                            )}
                            disabled={complete.isPending}
                          >
                            <Check size={12} /> Terminer
                          </button>
                          <button
                            className="ds-btn ds-btn-g ds-btn-sm"
                            onClick={() => handleAction(() => cancel.mutateAsync(of.id), `Annuler ${of.reference} ?`)}
                            disabled={cancel.isPending}
                            style={{ color: 'var(--danger)' }}
                          >
                            <X size={12} />
                          </button>
                        </>
                      )}
                      {(of.statut === 'termine' || of.statut === 'annule') && (
                        <span style={{ fontSize: 11, color: 'var(--tx-3)' }}>
                          {of.statut === 'termine' && of.dateFin
                            ? `Fini ${new Date(of.dateFin).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}`
                            : '—'}
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div style={{
          marginTop: 14, padding: 12, borderRadius: 'var(--r-sm)',
          border: '1px dashed var(--bdr)', background: 'var(--surface)',
          textAlign: 'center', fontSize: 11, color: 'var(--tx-3)',
        }}>
          <Clock size={11} style={{ display: 'inline', marginRight: 4 }} />
          Sprint <strong>LB-B v2</strong> livré : OrdreFabrication + déduction stock auto via BOM + <strong>LotProduction</strong> (traçabilité QR) + estimation auto composition C20-C35 + cadence Dongyue QT18 (4320 U/h) + planning Haiyu 52 moules.
        </div>
      </div>

      <OrdreFormDialog open={dialogOpen} onClose={() => setDialogOpen(false)} />
      <LotsDialog ordre={lotsForOrdre} onClose={() => setLotsForOrdre(null)} />
    </>
  );
};

// ── Dialog création OF ────────────────────────────────────

const OrdreFormDialog = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
  const [produitId, setProduitId]         = useState('');
  const [quantite, setQuantite]           = useState('');
  const [datePlanifiee, setDatePlanifiee] = useState(new Date().toISOString().slice(0, 10));
  const [notes, setNotes]                 = useState('');
  const [commandeId, setCommandeId]       = useState('');
  const [mouleId, setMouleId]             = useState('');
  const [equipeProductionId, setEquipeId] = useState('');
  const [dateCoulage, setDateCoulage]     = useState('');
  const [error, setError]                 = useState<string | null>(null);

  const { data: produitsData } = useBetonProduits({ actif: true, limit: 200 });
  const { data: moulesData }    = useMoules({ limit: 100 });
  const { data: equipesData }   = useEquipesProduction();
  const { data: commandesData } = useCommandes({ statut: 'confirmee', limit: 50 });
  const create = useCreateOrdre();
  const produits  = produitsData?.data ?? [];
  const moules    = moulesData?.data ?? [];
  const equipes   = equipesData ?? [];
  const commandes = commandesData?.data ?? [];

  const selectedProduit = produits.find((p) => p.id === produitId);
  const delaiCure = selectedProduit?.delaiCureJours ?? null;

  useEffect(() => {
    if (open) {
      setProduitId(''); setQuantite(''); setNotes(''); setError(null);
      setCommandeId(''); setMouleId(''); setEquipeId(''); setDateCoulage('');
      setDatePlanifiee(new Date().toISOString().slice(0, 10));
    }
  }, [open]);

  // Pré-remplir moule par défaut quand on choisit un produit
  useEffect(() => {
    if (selectedProduit?.mouleIdDefault && !mouleId) {
      setMouleId(selectedProduit.mouleIdDefault);
    }
  }, [selectedProduit]); // eslint-disable-line react-hooks/exhaustive-deps

  const dateDecoffragePrevue = useMemo(() => {
    if (!dateCoulage || delaiCure == null) return null;
    const d = new Date(dateCoulage);
    d.setDate(d.getDate() + delaiCure);
    return d.toISOString().slice(0, 10);
  }, [dateCoulage, delaiCure]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await create.mutateAsync({
        produitId,
        quantite:      parseFloat(quantite || '0'),
        datePlanifiee,
        notes:         notes.trim() || undefined,
        commandeId:         commandeId || undefined,
        mouleId:            mouleId || undefined,
        equipeProductionId: equipeProductionId || undefined,
        dateCoulage:        dateCoulage || undefined,
      });
      onClose();
    } catch (err) {
      setError(extractError(err).message);
    }
  };

  const canSubmit = produitId && parseFloat(quantite || '0') > 0 && datePlanifiee;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Nouvel ordre de fabrication"
      subtitle="Référence auto OF-YYYY-NNN · statut planifié"
      size="lg"
    >
      <form onSubmit={onSubmit} className="space-y-3">
        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">{error}</div>
        )}

        <Field label="Produit catalogue *">
          {produits.length === 0 ? (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
              Aucun produit catalogue. Créez-en un d'abord dans Opérations → Catalogue produits.
            </div>
          ) : (
            <select
              value={produitId}
              onChange={(e) => setProduitId(e.target.value)}
              className="flex h-10 w-full rounded-md border border-gray-200 bg-white px-3 text-sm"
              required
            >
              <option value="">— Sélectionner —</option>
              {produits.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.code} · {p.designation}
                  {p.delaiCureJours != null ? ` (cure ${p.delaiCureJours}j)` : ''}
                </option>
              ))}
            </select>
          )}
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Quantité à produire *">
            <input
              type="number" min="0.01" step="0.01"
              value={quantite}
              onChange={(e) => setQuantite(e.target.value)}
              className="flex h-10 w-full rounded-md border border-gray-200 bg-white px-3 text-sm"
              required
              placeholder="100"
            />
          </Field>
          <Field label="Date prévue *">
            <input
              type="date"
              value={datePlanifiee}
              onChange={(e) => setDatePlanifiee(e.target.value)}
              className="flex h-10 w-full rounded-md border border-gray-200 bg-white px-3 text-sm"
              required
            />
          </Field>
        </div>

        {/* ── Section préfabriqué (Phase 1.3) ── */}
        <div className="rounded-md border border-emerald-200 bg-emerald-50/40 p-3 space-y-3">
          <div className="text-xs font-semibold text-emerald-800">⚙️ Préfabriqué — Affectation moule / équipe / cure</div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Moule (pré-rempli si produit en a un)">
              <select
                value={mouleId}
                onChange={(e) => setMouleId(e.target.value)}
                className="flex h-10 w-full rounded-md border border-gray-200 bg-white px-3 text-sm"
              >
                <option value="">— Aucun —</option>
                {moules.map((m) => (
                  <option key={m.id} value={m.id} disabled={m.statut === 'hors_service' || m.statut === 'maintenance'}>
                    {m.code} · {m.libelle}
                    {m.statut !== 'operationnel' ? ` [${m.statut}]` : ''}
                    {m.prochainEntretienCycles != null
                      ? ` · ${m.nbCyclesEffectues}/${m.prochainEntretienCycles} cycles`
                      : ''}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Équipe production">
              <select
                value={equipeProductionId}
                onChange={(e) => setEquipeId(e.target.value)}
                className="flex h-10 w-full rounded-md border border-gray-200 bg-white px-3 text-sm"
              >
                <option value="">— Aucune —</option>
                {equipes.filter((eq) => eq.actif).map((eq) => (
                  <option key={eq.id} value={eq.id}>
                    {eq.code} · {eq.nom}{eq.chefNom ? ` (${eq.chefNom})` : ''}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Date de coulage (= début cure)">
              <input
                type="date"
                value={dateCoulage}
                onChange={(e) => setDateCoulage(e.target.value)}
                className="flex h-10 w-full rounded-md border border-gray-200 bg-white px-3 text-sm"
              />
            </Field>
            <Field label="Date décoffrage prévue (auto)">
              <input
                type="text"
                value={
                  dateDecoffragePrevue
                    ? `${new Date(dateDecoffragePrevue).toLocaleDateString('fr-FR')} (+${delaiCure}j cure)`
                    : delaiCure == null
                      ? '— produit sans délai cure —'
                      : '— renseigner date coulage —'
                }
                readOnly
                className="flex h-10 w-full rounded-md border border-emerald-200 bg-emerald-50 px-3 text-sm text-emerald-900"
              />
            </Field>
          </div>

          <Field label="Commande client source (optionnel)">
            <select
              value={commandeId}
              onChange={(e) => setCommandeId(e.target.value)}
              className="flex h-10 w-full rounded-md border border-gray-200 bg-white px-3 text-sm"
            >
              <option value="">— Aucune (OF sur stock) —</option>
              {commandes.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.reference} · {c.client?.companyName ?? c.client?.fullName ?? '—'}
                  {c.dateLivraisonSouhaitee ? ` · livr. ${new Date(c.dateLivraisonSouhaitee).toLocaleDateString('fr-FR')}` : ''}
                </option>
              ))}
            </select>
          </Field>
        </div>

        <Field label="Notes (optionnel)">
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            className="flex w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm"
            placeholder="Chantier, client, contrainte technique…"
          />
        </Field>

        <EstimationBox produitId={produitId} quantite={parseFloat(quantite || '0')} />

        <div className="flex justify-end gap-2 border-t border-gray-100 pt-3">
          <button type="button" className="ds-btn ds-btn-g" onClick={onClose}>Annuler</button>
          <button
            type="submit"
            className="ds-btn ds-btn-p"
            disabled={!canSubmit || create.isPending}
            style={{ opacity: !canSubmit || create.isPending ? 0.5 : 1, cursor: !canSubmit || create.isPending ? 'not-allowed' : 'pointer' }}
          >
            <ClipboardList size={14} /> {create.isPending ? 'Création…' : 'Créer OF'}
          </button>
        </div>
      </form>
    </Dialog>
  );
};

const Field = ({ label, children }: { label: string; children: ReactNode }) => (
  <div>
    <label className="mb-1 block text-xs font-medium text-gray-600">{label}</label>
    {children}
  </div>
);

// ── Estimation panel (composition + cadence + planning selon famille) ────

const EstimationBox = ({ produitId, quantite }: { produitId: string; quantite: number }) => {
  const { data, isLoading } = useEstimation(produitId, quantite);
  if (!produitId || quantite <= 0) return null;

  if (isLoading) return (
    <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-600">
      📊 Calcul de l'estimation…
    </div>
  );
  if (!data) return null;

  return (
    <div className="rounded-md border border-blue-200 bg-blue-50 px-3 py-3 text-xs text-blue-900 space-y-1">
      <div className="font-semibold">📊 Estimation — {data.produit.designation}</div>

      {data.composition && (
        <div className="font-mono space-y-0.5 pl-2">
          <div>Classe béton détectée : <strong>C{data.composition.classeBeton}</strong></div>
          <div>Ciment : {formatNumber(data.composition.cimentKg)} kg</div>
          <div>Sable : {formatNumber(data.composition.sableKg)} kg</div>
          <div>Gravier : {formatNumber(data.composition.gravierKg)} kg</div>
          <div>Eau : {formatNumber(data.composition.eauL)} L</div>
          <div>Adjuvant : {formatNumber(data.composition.adjuvantL)} L</div>
          <div>Total : <strong>{formatNumber(data.composition.totalKg)} kg</strong> · E/C={data.composition.rapportEC}
            {!data.composition.conforme && <span className="text-amber-700"> ⚠ E/C &gt; 0.6</span>}
          </div>
        </div>
      )}

      {data.cadence && (
        <div className="font-mono space-y-0.5 pl-2">
          <div>Machine : <strong>Dongyue QT18-15</strong> (rendement {data.cadence.rendementPct}%)</div>
          <div>Cadence : {formatNumber(data.cadence.cadencePuParHeure)} u/h · {formatNumber(data.cadence.cadencePuParJour)} u/jour</div>
          <div>Durée estimée : <strong>{data.cadence.dureeHeures} h</strong> ({data.cadence.dureeJoursOuvres} jours)</div>
        </div>
      )}

      {data.planning && (
        <div className="font-mono space-y-0.5 pl-2">
          <div>Machine : <strong>Haiyu coffrage</strong> ({data.planning.moulesDispo} moules)</div>
          <div>Capacité : {data.planning.capaciteJournaliere} poteaux/jour (cycle 24h)</div>
          <div>Cycles nécessaires : {data.planning.cyclesNecessaires}</div>
          <div>Durée estimée : <strong>{data.planning.dureeJoursOuvres} jours</strong> ({data.planning.dureeHeures} h)</div>
        </div>
      )}

      {!data.composition && !data.cadence && !data.planning && (
        <div className="text-gray-600 italic">Famille produit {data.produit.famille} — pas d'estimation auto disponible</div>
      )}
    </div>
  );
};

// ── Lots dialog (création + liste + actions) ──────────────────────────

const LotsDialog = ({ ordre, onClose }: { ordre: OrdreFabrication | null; onClose: () => void }) => {
  const open = ordre !== null;
  const { data, isLoading } = useLotsByOrdre(ordre?.id);
  const create  = useCreateLot();
  const update  = useUpdateLot();
  const remove  = useDeleteLot();

  const [quantite, setQuantite] = useState('');
  const [notes, setNotes]       = useState('');
  const [error, setError]       = useState<string | null>(null);
  const [verifLot, setVerifLot] = useState<LotProduction | null>(null);
  const [dncLot,   setDncLot]   = useState<LotProduction | null>(null);
  const [placerLot, setPlacerLot] = useState<LotProduction | null>(null);

  const isPoteauBA = ordre?.produit?.famille === 'POTEAUX_BA';

  useEffect(() => {
    if (open) { setQuantite(''); setNotes(''); setError(null); }
  }, [open]);

  if (!ordre) return null;

  const lots = data?.data ?? [];
  const totalLots = lots.reduce((s, l) => s + parseFloat(l.quantite), 0);

  const onCreate = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await create.mutateAsync({
        ordreId: ordre.id,
        payload: { quantite: parseFloat(quantite || '0'), notes: notes.trim() || undefined },
      });
      setQuantite(''); setNotes('');
    } catch (err) { setError(extractError(err).message); }
  };

  const onValidate = async (id: string) => {
    if (!(await demander.confirmer({ titre: 'Valider ce lot (contrôle qualité OK) ?', confirmer: 'Valider', danger: true }))) return;
    setError(null);
    try { await update.mutateAsync({ id, payload: { statut: 'valide', controleQualiteOk: true } }); }
    catch (err) { setError(extractError(err).message); }
  };

  const onReject = async (id: string) => {
    if (!(await demander.confirmer({ titre: 'Rejeter ce lot ?', message: 'Non-conforme.', confirmer: 'Rejeter', danger: true }))) return;
    setError(null);
    try { await update.mutateAsync({ id, payload: { statut: 'rejete', controleQualiteOk: false } }); }
    catch (err) { setError(extractError(err).message); }
  };

  const onRemove = async (id: string, lotNumber: string) => {
    if (!(await demander.confirmer({ titre: `Supprimer le lot ${lotNumber} définitivement ?`, confirmer: 'Supprimer', danger: true }))) return;
    setError(null);
    try { await remove.mutateAsync(id); }
    catch (err) { setError(extractError(err).message); }
  };

  const STATUT_LOT_BADGE: Record<StatutLot, string> = {
    en_production: 'ds-bd ds-bd-i',
    valide:        'ds-bd ds-bd-s',
    rejete:        'ds-bd ds-bd-d',
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={`Lots — ${ordre.reference}`}
      subtitle={`${ordre.produit?.designation ?? ''} · ${formatNumber(parseFloat(ordre.quantite))} ${ordre.produit?.uniteVente ?? ''} planifié · ${lots.length} lot${lots.length > 1 ? 's' : ''} (${formatNumber(totalLots)} produit)`}
      size="lg"
    >
      <div className="space-y-3">
        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
            <AlertTriangle className="inline size-3 mr-1" />{error}
          </div>
        )}

        {/* Formulaire création lot */}
        <form onSubmit={onCreate} className="rounded-md border border-gray-200 bg-gray-50 p-3 space-y-2">
          <div className="text-xs font-semibold text-gray-700">Nouveau lot</div>
          <div className="grid grid-cols-3 gap-2">
            <input
              type="number" min="0.01" step="0.01"
              value={quantite}
              onChange={(e) => setQuantite(e.target.value)}
              placeholder="Quantité"
              className="ds-fi"
              required
            />
            <input
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Notes (optionnel)"
              className="ds-fi"
            />
            <button type="submit" className="ds-btn ds-btn-p ds-btn-sm" disabled={!quantite || create.isPending}>
              <Plus size={12} /> Créer
            </button>
          </div>
          <div className="text-[10px] text-gray-500">N° auto L-YYYY-NNNNN · QR code traçabilité auto · statut initial "en production"</div>
        </form>

        {/* Liste lots */}
        {isLoading ? (
          <div className="text-center text-xs text-gray-500 py-4">Chargement…</div>
        ) : lots.length === 0 ? (
          <div className="text-center text-xs text-gray-500 py-4">Aucun lot créé pour cet OF</div>
        ) : (
          <table className="ds-tbl">
            <thead>
              <tr>
                <th style={{ width: 140 }}>N° Lot</th>
                <th className="ds-am" style={{ width: 100 }}>Quantité</th>
                <th style={{ width: 100 }}>Production</th>
                <th style={{ width: 100 }}>Statut</th>
                <th style={{ width: 90 }}>QR</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {lots.map((l) => (
                <tr key={l.id}>
                  <td>
                    <span className="ds-ref-code">{l.lotNumber}</span>
                    {l.controleQualiteOk === true  && <span style={{ marginLeft: 4, color: 'var(--success)' }} title="CQ OK">✅</span>}
                    {l.controleQualiteOk === false && <span style={{ marginLeft: 4, color: 'var(--danger)' }}  title="CQ KO">❌</span>}
                  </td>
                  <td className="ds-am">
                    {formatNumber(parseFloat(l.quantite))}
                    {l.fc28Mesure && <div style={{ fontSize: 10, color: 'var(--tx-3)' }}>fc28 {l.fc28Mesure} MPa</div>}
                  </td>
                  <td style={{ fontSize: 12, color: 'var(--tx-2)' }}>{fmtDate(l.dateProduction)}</td>
                  <td><span className={STATUT_LOT_BADGE[l.statut]}>{STATUT_LOT_LABELS[l.statut]}</span></td>
                  <td>
                    <a href={l.qrCode} target="_blank" rel="noopener noreferrer" title={l.qrCode} className="ds-row-btn">
                      <QrCode size={13} />
                    </a>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      {l.statut === 'en_production' && (
                        <>
                          <button className="ds-btn ds-btn-g ds-btn-sm" onClick={() => onValidate(l.id)} disabled={update.isPending} style={{ color: 'var(--success)' }} title="Valider (CQ OK)">
                            <Check size={11} />
                          </button>
                          <button className="ds-btn ds-btn-g ds-btn-sm" onClick={() => onReject(l.id)} disabled={update.isPending} style={{ color: 'var(--danger)' }} title="Rejeter">
                            <X size={11} />
                          </button>
                        </>
                      )}
                      {isPoteauBA && l.statut !== 'rejete' && (
                        <button className="ds-btn ds-btn-g ds-btn-sm" onClick={() => setVerifLot(l)} title="Vérifier NF C 67-200" style={{ color: 'var(--info)' }}>
                          <ShieldCheck size={11} />
                        </button>
                      )}
                      {/* Sprint 2 — Placer en parc (lot validé + pas encore en parc) */}
                      {l.statut === 'valide' && (!l.dateEntreeParc || l.dateSortieParc) && (
                        <button className="ds-btn ds-btn-p ds-btn-sm" onClick={() => setPlacerLot(l)} title="Placer en parc" style={{ background: 'var(--success)', color: 'white' }}>
                          <MapPin size={11} /> Parc
                        </button>
                      )}
                      {/* Lot déjà en parc → badge zone, non-cliquable */}
                      {l.statut === 'valide' && l.dateEntreeParc && !l.dateSortieParc && (
                        <span title={`En parc depuis ${new Date(l.dateEntreeParc).toLocaleDateString('fr-FR')}`}
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 10, color: 'var(--success)', fontWeight: 600 }}>
                          <MapPin size={10} /> {l.zoneParc ?? 'Parc'}
                        </span>
                      )}
                      {(l.statut === 'rejete' || l.controleQualiteOk === false) && (
                        <button className="ds-btn ds-btn-g ds-btn-sm" onClick={() => setDncLot(l)} title="Créer DNC" style={{ color: 'var(--warning)' }}>
                          <FileWarning size={11} />
                        </button>
                      )}
                      {/* SIMG-TRACABILITE-PIECES-2026-001 — accès à la liste des pièces + feuille A4 */}
                      <a
                        href={`/operations/production/lots/${l.id}/pieces`}
                        className="ds-btn ds-btn-g ds-btn-sm"
                        title="Voir les pièces (traçabilité)"
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}
                      >
                        <QrCode size={11} /> Pièces
                      </a>
                      <button className="ds-btn ds-btn-g ds-btn-sm" onClick={() => onRemove(l.id, l.lotNumber)} disabled={remove.isPending} title="Supprimer">
                        <X size={11} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <div className="flex justify-end pt-2 border-t border-gray-100">
          <button type="button" className="ds-btn ds-btn-g" onClick={onClose}>Fermer</button>
        </div>
      </div>

      <VerifierNFDialog lot={verifLot} onClose={() => setVerifLot(null)} />
      <CreateDNCDialog  lot={dncLot}   onClose={() => setDncLot(null)} />
      <PlacerParcDialog lot={placerLot} onClose={() => setPlacerLot(null)} />
    </Dialog>
  );
};

// ── Sous-dialog vérification NF C 67-200 ─────────────────────────────

const VerifierNFDialog = ({ lot, onClose }: { lot: LotProduction | null; onClose: () => void }) => {
  const open = lot !== null;
  const verifier = useVerifierNF();
  const [fc28, setFc28]     = useState('');
  const [longueur, setLong] = useState('');
  const [charge, setCharge] = useState('');
  const [error, setError]   = useState<string | null>(null);
  const [result, setResult] = useState<{ conforme: boolean; raisons: string[] } | null>(null);

  useEffect(() => {
    if (open) {
      setFc28(lot?.fc28Mesure ?? ''); setLong(''); setCharge(''); setError(null); setResult(null);
    }
  }, [open, lot]);

  if (!lot) return null;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null); setResult(null);
    try {
      const res = await verifier.mutateAsync({
        lotId: lot.id,
        payload: {
          fc28Mesure:       fc28     ? parseFloat(fc28)     : undefined,
          longueurMesureeM: longueur ? parseFloat(longueur) : undefined,
          chargeRuptureDaN: charge   ? parseFloat(charge)   : undefined,
        },
      });
      setResult({ conforme: res.resultat.conforme, raisons: res.resultat.raisonsNonConformite });
    } catch (err) { setError(extractError(err).message); }
  };

  return (
    <Dialog open={open} onClose={onClose} title={`Vérification NF C 67-200 — ${lot.lotNumber}`} subtitle="Poteaux BA · fc28 ≥ 35 MPa · longueur ±5mm · charge ≥ 2× effort nominal" size="md">
      <form onSubmit={onSubmit} className="space-y-3">
        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
            <AlertTriangle className="inline size-3 mr-1" />{error}
          </div>
        )}

        <Field label="fc28 mesurée (MPa)">
          <input type="number" min="0" step="0.1" value={fc28} onChange={(e) => setFc28(e.target.value)} className="ds-fi" style={{ width: '100%' }} placeholder="38.5" />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Longueur mesurée (m)">
            <input type="number" min="0" step="0.001" value={longueur} onChange={(e) => setLong(e.target.value)} className="ds-fi" style={{ width: '100%' }} placeholder="10.002" />
          </Field>
          <Field label="Charge rupture (daN)">
            <input type="number" min="0" step="1" value={charge} onChange={(e) => setCharge(e.target.value)} className="ds-fi" style={{ width: '100%' }} placeholder="650" />
          </Field>
        </div>

        {result && (
          <div className={`rounded-md border px-3 py-2 text-xs ${result.conforme ? 'border-emerald-200 bg-emerald-50 text-emerald-900' : 'border-red-200 bg-red-50 text-red-900'}`}>
            <div className="font-semibold mb-1">
              {result.conforme ? '✅ CONFORME NF C 67-200' : '❌ NON CONFORME'}
            </div>
            {result.raisons.length > 0 && (
              <ul className="list-disc pl-4 space-y-0.5">
                {result.raisons.map((r) => <li key={r}>{r}</li>)}
              </ul>
            )}
            <div className="mt-2 text-[10px] opacity-70">Statut du lot mis à jour automatiquement ({result.conforme ? 'validé' : 'rejeté'}) + notes horodatées</div>
          </div>
        )}

        <div className="flex justify-end gap-2 border-t border-gray-100 pt-3">
          <button type="button" className="ds-btn ds-btn-g" onClick={onClose}>Fermer</button>
          <button type="submit" className="ds-btn ds-btn-p" disabled={(!fc28 && !longueur && !charge) || verifier.isPending}>
            <ShieldCheck size={14} /> {verifier.isPending ? 'Vérification…' : 'Vérifier'}
          </button>
        </div>
      </form>
    </Dialog>
  );
};

// ── Sous-dialog création DNC ────────────────────────────────────────

const CreateDNCDialog = ({ lot, onClose }: { lot: LotProduction | null; onClose: () => void }) => {
  const open = lot !== null;
  const create = useCreateDNC();
  const [cause, setCause]   = useState('');
  const [action, setAction] = useState('');
  const [error, setError]   = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setCause(`Lot ${lot?.lotNumber} non conforme — `);
      setAction('');
      setError(null);
    }
  }, [open, lot]);

  if (!lot) return null;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await create.mutateAsync({ lotId: lot.id, cause, actionCorrective: action || undefined });
      onClose();
    } catch (err) { setError(extractError(err).message); }
  };

  return (
    <Dialog open={open} onClose={onClose} title={`Déclaration Non-Conformité — ${lot.lotNumber}`} subtitle="Référence auto DNC-YYYY-NNN · statut initial 'Ouverte'" size="md">
      <form onSubmit={onSubmit} className="space-y-3">
        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">{error}</div>
        )}

        <Field label="Cause de la non-conformité *">
          <textarea value={cause} onChange={(e) => setCause(e.target.value)} rows={3} className="ds-fi" style={{ width: '100%' }} placeholder="ex: fc28 mesurée 28 MPa < seuil 35 MPa NF C 67-200" required />
        </Field>

        <Field label="Action corrective (optionnel)">
          <textarea value={action} onChange={(e) => setAction(e.target.value)} rows={2} className="ds-fi" style={{ width: '100%' }} placeholder="ex: Recyclage en granulats — formation équipe sur dosage ciment" />
        </Field>

        <div className="flex justify-end gap-2 border-t border-gray-100 pt-3">
          <button type="button" className="ds-btn ds-btn-g" onClick={onClose}>Annuler</button>
          <button type="submit" className="ds-btn ds-btn-p" disabled={!cause.trim() || create.isPending}>
            <FileWarning size={14} /> {create.isPending ? 'Création…' : 'Créer DNC'}
          </button>
        </div>
      </form>
    </Dialog>
  );
};

// ── Sous-dialog : placer un lot validé en parc (Sprint 2 Module 6) ────────

const PlacerParcDialog = ({ lot, onClose }: { lot: LotProduction | null; onClose: () => void }) => {
  const open = lot !== null;
  const placer = usePlacerLotParc();
  const { data: inventaire } = useInventaireParc();
  const [zoneParc, setZoneParc]               = useState('');
  const [zoneCustom, setZoneCustom]           = useState('');
  const [emplacementParc, setEmplacementParc] = useState('');
  const [motif, setMotif]                     = useState('');
  const [error, setError]                     = useState<string | null>(null);

  // Liste zones existantes (depuis inventaire) + saisie libre
  const zonesExistantes = (inventaire?.zones ?? []).map((z) => z.zone).filter((z) => z !== '(sans zone)');

  useEffect(() => {
    if (open) {
      setZoneParc(zonesExistantes[0] ?? '__custom__');
      setZoneCustom(''); setEmplacementParc(''); setMotif(''); setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!lot) return null;

  const zoneFinale = zoneParc === '__custom__' ? zoneCustom.trim() : zoneParc;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!zoneFinale) { setError('Zone requise'); return; }
    try {
      await placer.mutateAsync({
        lotId: lot.id,
        payload: {
          zoneParc:        zoneFinale,
          emplacementParc: emplacementParc.trim() || undefined,
          motif:           motif.trim() || undefined,
        },
      });
      onClose();
    } catch (err) { setError(extractError(err).message); }
  };

  return (
    <Dialog open={open} onClose={onClose} title={`Placer en parc — ${lot.lotNumber}`} subtitle={`${formatNumber(parseFloat(lot.quantite))} unités · QC validé`} size="sm">
      <form onSubmit={onSubmit} className="space-y-3">
        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
            <AlertTriangle className="inline size-3 mr-1" />{error}
          </div>
        )}

        <Field label="Zone parc *">
          <select value={zoneParc} onChange={(e) => setZoneParc(e.target.value)} required
            className="flex h-10 w-full rounded-md border border-gray-200 bg-white px-3 text-sm">
            {zonesExistantes.length > 0 && (
              <optgroup label="Zones existantes">
                {zonesExistantes.map((z) => <option key={z} value={z}>{z}</option>)}
              </optgroup>
            )}
            <option value="__custom__">+ Nouvelle zone…</option>
          </select>
        </Field>

        {zoneParc === '__custom__' && (
          <Field label="Nom de la nouvelle zone *">
            <input value={zoneCustom} onChange={(e) => setZoneCustom(e.target.value)} required autoFocus
              placeholder="ex: A1, B2, Z-EXP"
              className="flex h-10 w-full rounded-md border border-gray-200 bg-white px-3 text-sm" />
          </Field>
        )}

        <Field label="Emplacement (optionnel)">
          <input value={emplacementParc} onChange={(e) => setEmplacementParc(e.target.value)}
            placeholder="ex: Rangée 3 niveau 2"
            className="flex h-10 w-full rounded-md border border-gray-200 bg-white px-3 text-sm" />
        </Field>

        <Field label="Motif (optionnel)">
          <input value={motif} onChange={(e) => setMotif(e.target.value)}
            placeholder="ex: Stockage tampon avant expédition CIE"
            className="flex h-10 w-full rounded-md border border-gray-200 bg-white px-3 text-sm" />
        </Field>

        <div className="rounded-md border border-emerald-200 bg-emerald-50 p-2 text-xs text-emerald-800">
          ✓ Le lot rejoint le parc avec audit complet (MouvementParc type=entree). Sera disponible pour expédition.
        </div>

        <div className="flex justify-end gap-2 border-t border-gray-100 pt-3">
          <button type="button" className="ds-btn ds-btn-g" onClick={onClose}>Annuler</button>
          <button type="submit" className="ds-btn ds-btn-p" disabled={!zoneFinale || placer.isPending}>
            <MapPin size={14} /> {placer.isPending ? 'Placement…' : 'Placer en parc'}
          </button>
        </div>
      </form>
    </Dialog>
  );
};

